import { isEmpty, propOr } from "ramda";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import { usePeopleActions } from "store/PeopleSlice";

const usePeople = () => {
  const {
    loadPeople,
    loadCelebrations,
    loadAdditionalPeople,
    createPerson,
    deletePerson,
    addHeadToPerson,
    resetPeople,
    updatePerson,
    addPersonToUpdate,
    resetPeopleToUpdate,
  } = usePeopleActions();

  const { people, meta, peopleToUpdate, celebrations } = useSelector((state) => state.PeopleSlice);

  const getPeopleCalendar = async () => {
    if (!isEmpty(celebrations)) {
      return Promise.resolve(celebrations);
    }

    const PER_PAGE = 6;
    const PAGE = 1;

    const peopleParams = {
      anniversaryParams: {
        page: { number: PAGE, size: PER_PAGE },
        filter: {
          calendar: "only-anniversaries",
          "start-date": dayjs().format("YYYY-MM-DD"),
        },
      },
      birthdayParams: {
        page: { number: PAGE, size: PER_PAGE },
        filter: {
          calendar: "only-birthdays",
          "start-date": dayjs().format("YYYY-MM-DD"),
        },
      },
    };

    const loadedPeople = await loadCelebrations(peopleParams);

    return loadedPeople;
  };

  const getPeopleCalendarBirthday = async () => {
    const peopleCalendar = await getPeopleCalendar();
    return peopleCalendar.filter((person) => person.dateType === "birthday");
  };

  const personAttributes = (personParams, head) => ({
    data: {
      type: "people",
      attributes: personParams,
      relationships: {
        heads: {
          data: [
            {
              type: "heads",
              id: head.id,
            },
          ],
        },
      },
    },
  });

  const personUpdateAttributes = (person) => {
    const { name, relation, birthday, anniversary, defaultHead, newDefaultHeadId } = person;
    return {
      data: {
        id: person.id,
        type: "people",
        attributes: {
          name,
          relation,
          birthday,
          anniversary,
          defaultHeadId: newDefaultHeadId,
        },
        relationships: {
          defaultHead: {
            data: {
              id: defaultHead ? defaultHead.id : newDefaultHeadId,
              type: "default-heads",
            },
          },
        },
      },
    };
  };

  return {
    loadPeople,
    loadCelebrations,
    meta,
    loadAdditionalPeople,
    resetPeople,
    deletePerson: (personId) => {
      const params = { deleteHeads: true };
      return deletePerson(personId, params);
    },
    createPerson: (personParams, head) => {
      const attributes = personAttributes(personParams, head);
      return createPerson(attributes);
    },
    updatePerson: (person) => {
      const params = personUpdateAttributes(person);
      return updatePerson(person.id, params);
    },
    addHeadToPerson,
    people,
    totalPeopleCount: propOr(people.count, "totalCount", meta),
    peopleToUpdate,
    celebrations,
    addPersonToUpdate,
    resetPeopleToUpdate,
    getPeopleCalendar,
    getPeopleCalendarBirthday,
  };
};

export default usePeople;
